import { useEffect, useMemo, useState } from 'react';
import Card from 'components/card';
import { getSupplierPurchaseStats } from 'interfaces/supplier';

type PendingItem = {
    purchaseId: string;
    purchaseNumber: string;
    purchaseDate: string;
    deliveryPurchase: string;
    status: string;
    productId: string;
    productName: string;
    unity: string;
    orderedQuantity: number;
    receivedQuantity: number;
    remainingQuantity: number;
};

const getStatusBadge = (status: string) => {
    if (status === 'partial') {
        return (
            <span className="inline-flex items-center whitespace-nowrap rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
                Partiel
            </span>
        );
    }
    return (
        <span className="inline-flex items-center whitespace-nowrap rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
            En attente
        </span>
    );
};

const formatDate = (d?: string) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const RemainingBadge = ({ item }: { item: PendingItem }) => {
    const ratio = item.orderedQuantity > 0 ? item.receivedQuantity / item.orderedQuantity : 0;
    return (
        <div className="min-w-[110px]">
            <p className="text-sm font-bold text-orange-600">
                {item.remainingQuantity} {item.unity || ''}
                <span className="ml-1 text-xs font-normal text-gray-400">
                    / {item.orderedQuantity}
                </span>
            </p>
            {ratio > 0 && (
                <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-orange-400" style={{ width: `${ratio * 100}%` }} />
                </div>
            )}
        </div>
    );
};

export default function PendingArrivals(props: { id: string }) {
    const { id } = props;
    const [items, setItems] = useState<PendingItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [visibleItems, setVisibleItems] = useState(8);

    const fetchStats = async () => {
        if (!id) return;
        setLoading(true);
        const res = await getSupplierPurchaseStats(id);
        if (res) setItems(res.pendingItems || []);
        setLoading(false);
    };

    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const rows = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.trim().toLowerCase();
        return items.filter(
            (i) =>
                i.productName?.toLowerCase().includes(q) ||
                i.purchaseNumber?.toLowerCase().includes(q)
        );
    }, [items, search]);

    return (
        <Card extra={'w-full h-full px-6 pb-6 border'}>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h4 className="text-lg font-medium text-navy-700 dark:text-white">En attente d'arrivage</h4>
                    <p className="mt-1 text-sm text-gray-600">
                        {items.length} ligne{items.length !== 1 ? 's' : ''} de commande encore en attente de réception
                    </p>
                </div>
                {items.length > 0 && (
                    <input
                        type="text"
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setVisibleItems(8);
                        }}
                        className="w-48 rounded-md border border-gray-200 px-2 py-1 text-sm outline-none focus:border-blue-400 dark:bg-navy-800 dark:text-white"
                    />
                )}
            </div>

            {loading ? (
                <div className="flex h-32 items-center justify-center">
                    <p className="text-gray-400 dark:text-white">Chargement...</p>
                </div>
            ) : rows.length === 0 ? (
                <div className="flex h-32 items-center justify-center">
                    <p className="text-gray-400 dark:text-white">
                        {search ? 'Aucun résultat' : "Aucun arrivage en attente"}
                    </p>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="mt-2 hidden overflow-x-auto md:block">
                        <table className="w-full">
                            <thead>
                                <tr className="!border-px !border-gray-400">
                                    <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                        <p className="text-xs font-bold text-gray-600 dark:text-white">Produit</p>
                                    </th>
                                    <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                        <p className="text-xs font-bold text-gray-600 dark:text-white">Commande</p>
                                    </th>
                                    <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                        <p className="text-xs font-bold text-gray-600 dark:text-white">Qté restante</p>
                                    </th>
                                    <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                        <p className="text-xs font-bold text-gray-600 dark:text-white">Dates</p>
                                    </th>
                                    <th className="border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30">
                                        <p className="text-xs font-bold text-gray-600 dark:text-white">Statut</p>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.slice(0, visibleItems).map((item, idx) => (
                                    <tr
                                        key={`${item.purchaseId}-${item.productId}-${idx}`}
                                        className="hover:bg-gray-50 dark:hover:bg-navy-700"
                                    >
                                        <td className="max-w-[200px] border-white/0 py-2 px-2">
                                            <p className="truncate text-sm font-medium text-navy-700 dark:text-white" title={item.productName}>
                                                {item.productName}
                                            </p>
                                        </td>
                                        <td className="border-white/0 py-2 px-2">
                                            <p className="truncate text-sm text-gray-500 dark:text-white">
                                                {item.purchaseNumber || '-'}
                                            </p>
                                        </td>
                                        <td className="border-white/0 py-2 px-2">
                                            <RemainingBadge item={item} />
                                        </td>
                                        <td className="whitespace-nowrap border-white/0 py-2 px-2 text-xs text-gray-500 dark:text-white">
                                            <p>Cmd : {formatDate(item.purchaseDate)}</p>
                                            <p>Livr. : {formatDate(item.deliveryPurchase)}</p>
                                        </td>
                                        <td className="border-white/0 py-2 px-2">{getStatusBadge(item.status)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="mt-2 space-y-2 md:hidden">
                        {rows.slice(0, visibleItems).map((item, idx) => (
                            <div
                                key={`${item.purchaseId}-${item.productId}-${idx}`}
                                className="rounded-xl border border-gray-100 p-3 shadow-sm dark:border-white/10"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">
                                            {item.productName}
                                        </p>
                                        <p className="text-xs text-gray-400">{item.purchaseNumber || '-'}</p>
                                    </div>
                                    {getStatusBadge(item.status)}
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                    <RemainingBadge item={item} />
                                    <div className="text-right text-xs text-gray-500 dark:text-white">
                                        <p>Cmd : {formatDate(item.purchaseDate)}</p>
                                        <p>Livr. : {formatDate(item.deliveryPurchase)}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {rows.length > visibleItems && (
                        <div className="mt-4 flex justify-center">
                            <button
                                onClick={() => setVisibleItems(visibleItems + 10)}
                                className="rounded-lg border border-toola-500 px-4 py-2 text-sm font-medium text-toola-500 transition-colors hover:bg-toola-50"
                            >
                                Voir plus ({rows.length - visibleItems} restants)
                            </button>
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}
